import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api-client';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { useAuth } from '@/lib/auth';
import { usePageTitle } from '@/hooks/use-page-title';
import { useHeaderAction } from '@/lib/header-action-context';
import { useStripeConfig } from '@/hooks/use-stripe-config';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Store,
    Zap,
    ShoppingCart,
    DollarSign,
    Activity,
    Layers,
    ArrowRight,
    Sparkles,
    Check,
    X,
} from 'lucide-react';
import { capabilityColors, getCapabilityColors, getStatusStyle, capabilityTitleIconBackground } from '@/lib/capability-colors';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Product {
    id: string;
    title: string;
    summary?: string;
    description?: string;
    icon_url?: string;
    listing_status: string;
    trial_days?: number;
}

interface FeaturePrice {
    id: string;
    product_id: string;
    capability_key?: string;
    capability_name?: string;
    capability_type?: string;
    capability_description?: string;
    description?: string;
    metric?: string;
    tier_name?: string;
    pricing_model: string;
    amount_cents?: number;
    currency: string;
    billing_interval: string;
    usage_limit?: number;
    overage_unit_amount_cents?: number;
    is_base_price: boolean;
    stripe_price_id?: string;
    stripe_feature_id?: string;
    status: string;
}

interface CheckoutResponse {
    client_secret?: string;
    url?: string;
}

// ---------------------------------------------------------------------------
// Constants & Helpers
// ---------------------------------------------------------------------------

const LOG_PREFIX = 'MarketplaceBrowsePage';

function formatCents(cents?: number): string {
    if (cents == null) return '—';
    return `$${(cents / 100).toFixed(2)}`;
}

function pricingLabel(fp: FeaturePrice): string {
    switch (fp.pricing_model) {
        case 'flat':
            return `${formatCents(fp.amount_cents)}/${fp.billing_interval}`;
        case 'metered':
            return `${formatCents(fp.amount_cents)}/invocation`;
        case 'per_unit':
            return `${formatCents(fp.amount_cents)}/unit/${fp.billing_interval}`;
        default:
            return formatCents(fp.amount_cents);
    }
}

function pricingModelIcon(model: string) {
    switch (model) {
        case 'flat':
            return <DollarSign className="h-4 w-4" />;
        case 'metered':
            return <Activity className="h-4 w-4" />;
        case 'per_unit':
            return <Layers className="h-4 w-4" />;
        default:
            return <DollarSign className="h-4 w-4" />;
    }
}

function pricingSummary(features: FeaturePrice[]): string {
    const base = features.find((f) => f.is_base_price);
    const addons = features.filter((f) => !f.is_base_price);

    if (base && addons.length > 0) {
        return `Base + ${addons.length} add-on${addons.length > 1 ? 's' : ''}`;
    }
    if (base) {
        return `From ${formatCents(base.amount_cents)}/mo`;
    }
    if (addons.length > 0) {
        const hasFlat = addons.some((a) => a.pricing_model === 'flat');
        if (hasFlat) {
            const cheapest = Math.min(
                ...addons.filter((a) => a.pricing_model === 'flat' && a.amount_cents != null).map((a) => a.amount_cents!),
            );
            return isFinite(cheapest) ? `From ${formatCents(cheapest)}/mo` : 'Pay per use';
        }
        return 'Pay per use';
    }
    return 'Free';
}

function pricingModelBadgeClasses(model: string): string {
    switch (model) {
        case 'flat':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
        case 'metered':
            return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
        case 'per_unit':
            return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
        default:
            return 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200';
    }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const productStyle = {
    accent: capabilityColors.subscription.border,
    iconBg: capabilityColors.subscription.bgLight,
    iconColor: capabilityColors.subscription.text,
};

function ProductCard({
    product,
    features,
    onViewDetails,
}: {
    product: Product;
    features: FeaturePrice[];
    onViewDetails: () => void;
}) {
    const featureCount = features.filter((f) => !f.is_base_price).length;
    const summary = pricingSummary(features);
    const badgeStyle = getStatusStyle(product.listing_status);

    return (
        <Card
            className={`relative w-full min-h-32 border border-slate-200 p-3 border-l-4 ${productStyle.accent} text-foreground/90 transition-all duration-200 hover:cursor-pointer hover:border-slate-300 hover:bg-slate-50 shadow-sm hover:shadow-md hover:shadow-blue-100/50`}
            onClick={onViewDetails}
        >
            {/* Top Right: Badge */}
            <div className="absolute right-2 top-2 z-10">
                <span
                    className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase ${badgeStyle}`}
                >
                    {product.listing_status}
                </span>
            </div>

            {/* Header: Icon + Title/Subtitle */}
            <div className="flex flex-row items-start gap-2">
                <div
                    className={`flex shrink-0 items-center justify-center rounded-md p-1.5 ${productStyle.iconBg} ${productStyle.iconColor}`}
                >
                    <Zap size={18} />
                </div>
                <div className="flex flex-1 flex-col min-w-0">
                    <div className="text-left text-sm font-bold text-slate-800 leading-tight truncate pr-12">
                        {product.title}
                    </div>
                    <div className="text-left text-[10px] text-slate-500 line-clamp-2">
                        {product.summary || 'No description available'}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="mt-auto flex items-center gap-1.5 border-t border-slate-100 pt-1.5 text-[10px] text-slate-500">
                <div className="flex items-center gap-1 text-slate-500">
                    <Sparkles size={10} />
                    <span>{summary}</span>
                </div>
                {featureCount > 0 && (
                    <span className="text-slate-400">
                        · {featureCount} feature{featureCount > 1 ? 's' : ''}
                    </span>
                )}
                <div className="ml-auto flex items-center gap-1 text-slate-400">
                    <ArrowRight size={10} />
                    <span>View details</span>
                </div>
            </div>
        </Card>
    );
}

// ---------------------------------------------------------------------------
// Product Detail Modal
// ---------------------------------------------------------------------------

function ProductDetailDialog({
    product,
    features,
    open,
    onOpenChange,
}: {
    product: Product | null;
    features: FeaturePrice[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const { currentOrganization, identity } = useAuth();
    const { data: stripeConfig } = useStripeConfig();
    const stripePromise = useMemo(
        () => (stripeConfig?.publishable_key ? loadStripe(stripeConfig.publishable_key) : null),
        [stripeConfig?.publishable_key],
    );
    const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<string>>(new Set());
    const [subscribing, setSubscribing] = useState(false);
    const [checkoutSecret, setCheckoutSecret] = useState<string | null>(null);

    const basePrice = features.find((f) => f.is_base_price);
    const addons = features.filter((f) => !f.is_base_price);
    const isFullyFeatureBased = !basePrice && addons.length > 0;

    useEffect(() => {
        if (open) {
            setSelectedFeatureIds(new Set(addons.map((a) => a.id)));
            setCheckoutSecret(null);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleFeature = (id: string) => {
        setSelectedFeatureIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const calculatedTotal = useMemo(() => {
        let total = 0;
        if (basePrice?.amount_cents) total += basePrice.amount_cents;
        for (const addon of addons) {
            if (selectedFeatureIds.has(addon.id) && addon.amount_cents && addon.pricing_model === 'flat') {
                total += addon.amount_cents;
            }
        }
        return total;
    }, [basePrice, addons, selectedFeatureIds]);

    const hasMeteredSelected = addons.some(
        (a) => selectedFeatureIds.has(a.id) && (a.pricing_model === 'metered' || a.pricing_model === 'per_unit'),
    );

    const handleSubscribe = async () => {
        if (!product) return;
        setSubscribing(true);
        try {
            // Always include base price when present; add selected addons
            const featurePriceIds = [
                ...(basePrice ? [basePrice.id] : []),
                ...Array.from(selectedFeatureIds),
            ];
            const payload = {
                product_id: product.id,
                feature_price_ids: featurePriceIds,
                subscriber_org_id: currentOrganization?.id || '',
                subscriber_name: currentOrganization?.name || identity?.name || '',
                subscriber_email: identity?.email || '',
                ui_mode: stripePromise ? 'embedded' : 'redirect',
                return_url: `${window.location.origin}/marketplace/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
                success_url: `${window.location.origin}/marketplace/checkout/success`,
                cancel_url: `${window.location.origin}/marketplace/checkout/cancel`,
            };

            const res = await api.post<CheckoutResponse>('/billing/checkout', payload);

            if (stripePromise && res.data.client_secret) {
                setCheckoutSecret(res.data.client_secret);
            } else if (res.data.url) {
                window.location.href = res.data.url;
            } else {
                toast.error('No checkout session returned');
            }
        } catch (err) {
            console.error(`${LOG_PREFIX}.handleSubscribe: failed`, err);
            toast.error('Failed to start checkout. Please try again.');
        } finally {
            setSubscribing(false);
        }
    };

    const canSubscribe =
        isFullyFeatureBased ? selectedFeatureIds.size > 0 : true;

    if (!product) return null;

    return (
        <SlideDrawer isOpen={open} onClose={() => onOpenChange(false)}>
                        {/* Header */}
                        <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50 shrink-0">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                        <Zap className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-slate-900">
                                            {checkoutSecret && stripePromise
                                                ? 'Complete Your Purchase'
                                                : product.title}
                                        </h2>
                                        <p className="text-sm text-slate-500">
                                            {checkoutSecret && stripePromise
                                                ? `Securely subscribe to ${product.title}`
                                                : product.description || product.summary || 'Subscribe to this product'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenChange(false); }}
                                    className="rounded-full p-1 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900"
                                    aria-label="Close"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto px-24 py-8 bg-white">
                            {checkoutSecret && stripePromise ? (
                                <div className="min-h-[400px]">
                                    <EmbeddedCheckoutProvider
                                        stripe={stripePromise}
                                        options={{ clientSecret: checkoutSecret }}
                                    >
                                        <EmbeddedCheckout />
                                    </EmbeddedCheckoutProvider>
                                </div>
                            ) : (
                                <div className="space-y-4 w-full">
                            {basePrice && (
                                <Card className="border-primary/20 bg-primary/5">
                                    <CardContent className="pt-4 pb-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Check className="h-4 w-4 text-primary" />
                                                <span className="font-medium text-sm">Base Subscription</span>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-2xl font-bold tracking-tight">
                                                    {formatCents(basePrice.amount_cents)}
                                                </span>
                                                <span className="text-muted-foreground text-sm ml-1">
                                                    /{basePrice.billing_interval}
                                                </span>
                                            </div>
                                        </div>
                                        {basePrice.usage_limit != null && (
                                            <p className="text-xs text-muted-foreground mt-1 ml-6">
                                                Includes {basePrice.usage_limit.toLocaleString()} invocations
                                            </p>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {addons.length > 0 && (
                                <>
                                    <div>
                                        <h4 className="text-sm font-semibold mb-4">
                                            {isFullyFeatureBased
                                                ? 'Select features to subscribe'
                                                : 'Feature add-ons'}
                                        </h4>
                                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                            {addons.map((addon) => {
                                                const selected = selectedFeatureIds.has(addon.id);
                                                const capType = (addon.capability_type ?? 'app') as string;
                                                const colors = getCapabilityColors(capType);
                                                const Icon = colors.icon;
                                                const capDescription = addon.capability_description || addon.description || null;
                                                const metricLabel = addon.metric
                                                    ? addon.metric.replace(/_/g, ' ')
                                                    : null;
                                                return (
                                                    <Card
                                                        key={addon.id}
                                                        className={`relative cursor-pointer p-5 min-h-30 border-l-4 transition-colors ${
                                                            selected
                                                                ? `${colors.border} border-primary/30 bg-primary/5 ring-1 ring-primary/20`
                                                                : `${colors.border} ${colors.hover} border border-slate-200`
                                                        }`}
                                                        onClick={() => toggleFeature(addon.id)}
                                                    >
                                                        <Checkbox
                                                            checked={selected}
                                                            onCheckedChange={() => toggleFeature(addon.id)}
                                                            className="absolute right-2 top-2 shrink-0"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <div className="flex items-center gap-3">
                                                            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${colors.bgLight} ${colors.text}`}>
                                                                <Icon size={20} className={colors.text} />
                                                            </div>
                                                            <div className="min-w-0 pr-8">
                                                                <p className="font-medium text-slate-900 truncate">
                                                                    {addon.capability_name || addon.capability_key || addon.tier_name || 'Feature'}
                                                                </p>
                                                                {capDescription && (
                                                                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                                                        {capDescription}
                                                                    </p>
                                                                )}
                                                                {addon.tier_name && (
                                                                    <p className="text-[10px] text-slate-400 mt-0.5">
                                                                        Tier: {addon.tier_name}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="mt-3 space-y-1">
                                                            <div className="flex items-baseline gap-2">
                                                                <span className="text-lg font-semibold text-slate-900">
                                                                    {pricingLabel(addon)}
                                                                </span>
                                                                <Badge
                                                                    className={`text-[10px] capitalize ${pricingModelBadgeClasses(addon.pricing_model)}`}
                                                                >
                                                                    {addon.pricing_model.replace(/_/g, ' ')}
                                                                </Badge>
                                                            </div>
                                                            <div className="space-y-0.5">
                                                                {addon.pricing_model === 'metered' && (
                                                                    <>
                                                                        {metricLabel && (
                                                                            <p className="text-xs text-slate-500">
                                                                                Billed per {metricLabel}
                                                                            </p>
                                                                        )}
                                                                        {addon.usage_limit != null && (
                                                                            <p className="text-xs text-slate-500">
                                                                                {addon.usage_limit.toLocaleString()} {metricLabel ?? 'units'} included
                                                                            </p>
                                                                        )}
                                                                        {addon.overage_unit_amount_cents != null && addon.overage_unit_amount_cents > 0 && (
                                                                            <p className="text-xs text-slate-500">
                                                                                Overage: {formatCents(addon.overage_unit_amount_cents)}/{metricLabel ? metricLabel.replace(/s$/, '') : 'unit'}
                                                                            </p>
                                                                        )}
                                                                    </>
                                                                )}
                                                                {addon.pricing_model === 'per_unit' && (
                                                                    <>
                                                                        {addon.usage_limit != null && (
                                                                            <p className="text-xs text-slate-500">
                                                                                Up to {addon.usage_limit.toLocaleString()} units/{addon.billing_interval}
                                                                            </p>
                                                                        )}
                                                                        {addon.overage_unit_amount_cents != null && addon.overage_unit_amount_cents > 0 && (
                                                                            <p className="text-xs text-slate-500">
                                                                                Overage: {formatCents(addon.overage_unit_amount_cents)}/unit
                                                                            </p>
                                                                        )}
                                                                    </>
                                                                )}
                                                                {addon.pricing_model === 'flat' && addon.billing_interval && (
                                                                    <p className="text-xs text-slate-500">
                                                                        Billed {addon.billing_interval === 'year' ? 'annually' : 'monthly'}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Card>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}

                            <Separator />

                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-muted-foreground">
                                    Estimated total
                                </span>
                                <div className="text-right">
                                    <span className="text-xl font-bold">{formatCents(calculatedTotal)}</span>
                                    <span className="text-muted-foreground text-sm">/mo</span>
                                    {hasMeteredSelected && (
                                        <p className="text-xs text-muted-foreground">+ usage-based charges</p>
                                    )}
                                </div>
                            </div>

                            {product.trial_days != null && product.trial_days > 0 && (
                                <p className="text-xs text-center text-muted-foreground">
                                    Includes a {product.trial_days}-day free trial
                                </p>
                            )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {!(checkoutSecret && stripePromise) && (
                            <div className="px-8 py-5 border-t border-slate-100 bg-slate-50/50 flex justify-end items-center gap-3 shrink-0">
                                <Button variant="outline" onClick={() => onOpenChange(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSubscribe}
                                    disabled={subscribing || !canSubscribe}
                                >
                                    {subscribing ? (
                                        'Starting checkout...'
                                    ) : (
                                        <>
                                            <ShoppingCart className="h-4 w-4 mr-1.5" />
                                            Subscribe
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}
        </SlideDrawer>
    );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const SORT_OPTIONS = [
    { key: 'name', label: 'Name' },
    { key: 'pricing', label: 'Pricing' },
    { key: 'features', label: 'Features' },
];

const PRICING_FILTER_OPTIONS = [
    { value: 'all', label: 'All Pricing' },
    { value: 'free', label: 'Free' },
    { value: 'flat', label: 'Flat Rate' },
    { value: 'metered', label: 'Pay Per Use' },
] as const;

export default function MarketplaceBrowsePage() {
    usePageTitle('Marketplace');
    const { setHeaderTitle } = useHeaderAction();
    const [products, setProducts] = useState<Product[]>([]);
    const [productFeatures, setProductFeatures] = useState<Record<string, FeaturePrice[]>>({});
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [loadingFeatures, setLoadingFeatures] = useState<string | null>(null);
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [pricingFilter, setPricingFilter] = useState('all');

    // Header icon + subtitle
    const titleIcon = useMemo(() => {
        const colors = getCapabilityColors('subscription');
        const iconSurfaceStyle = capabilityTitleIconBackground(colors.hex, true);
        return (
            <div
                className={`flex h-7 w-7 items-center justify-center rounded-md ${colors.text}`}
                style={iconSurfaceStyle}
            >
                <Store size={16} />
            </div>
        );
    }, []);

    useEffect(() => {
        setHeaderTitle({
            icon: titleIcon,
            title: 'Marketplace',
            subtitle: 'Browse and subscribe to available products and services.',
            intense: true,
        });
        return () => setHeaderTitle(null);
    }, [setHeaderTitle, titleIcon]);

    useEffect(() => {
        api.get('/billing/products?status=published')
            .then((res) => {
                const raw = res.data?.products ?? res.data;
                const list: Product[] = Array.isArray(raw) ? raw : [];
                setProducts(list);
                list.forEach((p) => fetchFeaturesForProduct(p.id));
            })
            .catch((err) => {
                console.error(`${LOG_PREFIX}.fetchProducts: failed`, err);
                toast.error('Failed to load marketplace products');
            })
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchFeaturesForProduct = async (productId: string) => {
        try {
            const res = await api.get(`/billing/products/${productId}/features`);
            const data = res.data;
            const features: FeaturePrice[] = data?.features ?? (Array.isArray(data) ? data : []);
            setProductFeatures((prev) => ({ ...prev, [productId]: features }));
        } catch (err) {
            console.error(`${LOG_PREFIX}.fetchFeaturesForProduct: failed for ${productId}`, err);
        }
    };

    const openProductDetail = useCallback(async (product: Product) => {
        setSelectedProduct(product);
        setDetailOpen(true);

        if (!productFeatures[product.id]) {
            setLoadingFeatures(product.id);
            await fetchFeaturesForProduct(product.id);
            setLoadingFeatures(null);
        }
    }, [productFeatures]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const filteredProducts = useMemo(() => {
        let result = products;

        if (pricingFilter !== 'all') {
            result = result.filter((p) => {
                const feats = productFeatures[p.id] ?? [];
                if (pricingFilter === 'free') return feats.length === 0;
                return feats.some((f) => f.pricing_model === pricingFilter);
            });
        }

        const q = searchQuery.trim().toLowerCase();
        if (q) {
            result = result.filter(
                (p) =>
                    p.title.toLowerCase().includes(q) ||
                    (p.summary && p.summary.toLowerCase().includes(q)),
            );
        }

        const sorted = [...result].sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case 'name':
                    cmp = a.title.localeCompare(b.title);
                    break;
                case 'pricing': {
                    const sumA = pricingSummary(productFeatures[a.id] ?? []);
                    const sumB = pricingSummary(productFeatures[b.id] ?? []);
                    cmp = sumA.localeCompare(sumB);
                    break;
                }
                case 'features': {
                    const countA = (productFeatures[a.id] ?? []).length;
                    const countB = (productFeatures[b.id] ?? []).length;
                    cmp = countA - countB;
                    break;
                }
                default:
                    cmp = a.title.localeCompare(b.title);
                    break;
            }
            return sortDirection === 'asc' ? cmp : -cmp;
        });

        return sorted;
    }, [products, searchQuery, pricingFilter, sortField, sortDirection, productFeatures]);

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-8" style={{ height: '100%' }}>
            <CapabilityListPanel<Product>
                items={filteredProducts}
                sortOptions={SORT_OPTIONS}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search products by name or description..."
                allTags={[]}
                selectedTags={new Set<string>()}
                onToggleTag={() => {}}
                onClearAllTags={() => {}}
                onSelectAllTags={() => {}}
                showTagsFilter={false}
                extraToolbar={
                    <Select value={pricingFilter} onValueChange={setPricingFilter}>
                        <SelectTrigger className={cn('w-44', listControlsGlassTrigger)}>
                            <SelectValue placeholder="All Pricing" />
                        </SelectTrigger>
                        <SelectContent>
                            {PRICING_FILTER_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                }
                isLoading={loading}
                loadingMessage="Loading marketplace products..."
                emptyStateVariant="glass"
                emptyMessage={
                    searchQuery ? 'No matching products' : 'No published products available'
                }
                renderCard={(p) => (
                    <div key={p.id} className="block">
                        <ProductCard
                            product={p}
                            features={productFeatures[p.id] || []}
                            onViewDetails={() => openProductDetail(p)}
                        />
                    </div>
                )}
            />

            <ProductDetailDialog
                product={selectedProduct}
                features={
                    selectedProduct
                        ? loadingFeatures === selectedProduct.id
                            ? []
                            : productFeatures[selectedProduct.id] || []
                        : []
                }
                open={detailOpen}
                onOpenChange={setDetailOpen}
            />
        </div>
    );
}
